package com.dotdrives.web.pages.forms

import com.dotdrives.model.FormPaymentReadiness
import com.dotdrives.controller.BuilderTab
import com.dotdrives.controller.FormBuilderController
import com.dotdrives.controller.FormBuilderUiState
import com.dotdrives.model.GivingFormStatus
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface FormBuilderPageProps : Props {
    var controller: FormBuilderController
    var state: FormBuilderUiState
    var canWrite: Boolean
    var orgId: String
    var orgName: String?
    var onBack: () -> Unit
}

val FormBuilderPage = FC<FormBuilderPageProps> { props ->
    val state = props.state
    val controller = props.controller
    val (editing, setEditing) = useState(true)
    val (section, setSection) = useState<String?>("fields")
    val (mobilePane, setMobilePane) = useState("editor")
    FormBuilderV2Styles()
    if (state.loading && state.form == null) {
        div { className = ClassName("forms-builder-tab-content"); DotSkeleton { height = "56px" }; DotSkeleton { height = "400px" } }
        return@FC
    }
    if (state.error != null && state.form == null) {
        DotBanner { message = state.error!!; onRetry = controller::retry }
        return@FC
    }
    val form = state.form ?: return@FC
    val canEdit = props.canWrite && !state.saving && !state.uploadingLogo
    fun openSection(id: String) {
        setEditing(true)
        setMobilePane("editor")
        controller.setActiveTab(BuilderTab.Design)
        setSection(id)
    }
    div {
        className = ClassName("forms-builder forms-v2" + if (editing) " forms-v2-editing forms-v2-mobile-$mobilePane" else " forms-v2-full")
        div {
            key = "toolbar".unsafeCast<react.Key>()
            className = ClassName("forms-v2-toolbar")
            div {
                className = ClassName("forms-v2-toolbar-title")
                button {
                    type = ButtonType.buttonType; className = ClassName("forms-v2-back")
                    onClick = { if (!state.dirty || kotlinx.browser.window.confirm("Discard unsaved changes and return to giving forms?")) props.onBack() }; +"← Giving forms"
                }
                span { className = ClassName("forms-v2-toolbar-divider"); ariaHidden = true }
                span { className = ClassName("forms-v2-name"); title = form.name; +form.name }
                DotPill { text = formStatusLabel(form.status); tone = formStatusTone(form.status) }
                span {
                    className = ClassName("forms-v2-save-state" + if (state.dirty) " forms-v2-unsaved" else "")
                    ariaLive = "polite".unsafeCast<react.dom.aria.AriaLive>()
                    +when { state.saving -> "Saving…"; state.dirty -> "● Unsaved changes"; else -> "Saved ✓" }
                }
            }
            div {
                className = ClassName("forms-v2-toolbar-actions")
                div {
                    className = ClassName("forms-v2-mode")
                    listOf(true to "Edit", false to "Full preview").forEach { (edit, label) ->
                        button {
                            type = ButtonType.buttonType; className = ClassName(if (editing == edit) "selected" else "")
                            ariaPressed = (editing == edit).unsafeCast<react.dom.aria.AriaPressed>()
                            onClick = { setEditing(edit); setMobilePane(if (edit) "editor" else "preview") }; +label
                        }
                    }
                }
                if (form.status == GivingFormStatus.LIVE) {
                    button {
                        type = ButtonType.buttonType; className = ClassName("forms-v2-toolbar-button")
                        onClick = { copyFormLink(props.orgId, form.publicSlug) }; +"Copy public link"
                    }
                }
                if (props.canWrite) {
                    button {
                        type = ButtonType.buttonType; className = ClassName("forms-v2-save")
                        disabled = !canEdit || !state.dirty
                        onClick = { if (canEdit && state.dirty) controller.save() }
                        +if (state.saving) "Saving…" else "Save changes"
                    }
                    if (form.status == GivingFormStatus.DRAFT) {
                        button {
                            type = ButtonType.buttonType; className = ClassName("forms-v2-publish")
                            disabled = !canEdit; onClick = { if (canEdit) controller.publish() }; +"Publish form"
                        }
                    }
                } else span { className = ClassName("forms-v2-readonly"); +"🔒 Read only" }
            }
        }
        if (state.paymentReadiness == FormPaymentReadiness.MIGRATION_PENDING || state.paymentReadiness == FormPaymentReadiness.RESTRICTED) {
            div { className = ClassName("forms-v2-error"); DotBanner { tone = com.dotdrives.design.DotTone.Info; message = state.paymentReadiness!!.message; className = "forms-inline-banner" } }
        }
        state.error?.let { error ->
            div { key = "error".unsafeCast<react.Key>(); className = ClassName("forms-v2-error"); DotBanner { message = error; className = "forms-inline-banner" } }
        }
        if (editing) {
            div {
                key = "mobile-nav".unsafeCast<react.Key>()
                className = ClassName("forms-v2-mobile-nav")
                listOf("editor" to "Editor", "preview" to "Preview").forEach { (id, label) ->
                    button {
                        type = ButtonType.buttonType; className = ClassName(if (mobilePane == id) "selected" else "")
                        ariaPressed = (mobilePane == id).unsafeCast<react.dom.aria.AriaPressed>()
                        onClick = { setMobilePane(id) }; +label
                    }
                }
            }
        }
        div {
            key = "builder-body".unsafeCast<react.Key>()
            className = ClassName("forms-builder-body")
            // Keep draft input identity while browsing tabs or preview; hidden removes it from focus navigation.
            div {
                hidden = (!editing).unsafeCast<web.html.Hidden>()
                className = ClassName("forms-v2-editor-mount")
                div {
                    className = ClassName("forms-v2-editor" + if (!props.canWrite) " forms-v2-editor-readonly" else "")
                    div {
                        className = ClassName("forms-builder-tabs")
                        listOf(BuilderTab.Design to "Design", BuilderTab.Settings to "Settings", BuilderTab.Receipts to "Receipts").forEach { (tab, label) ->
                            button {
                                type = ButtonType.buttonType
                                className = ClassName("forms-builder-tab" + if (state.activeTab == tab) " forms-builder-tab-active" else "")
                                ariaPressed = (state.activeTab == tab).unsafeCast<react.dom.aria.AriaPressed>()
                                onClick = { controller.setActiveTab(tab) }; +label
                            }
                        }
                    }
                    div {
                        className = ClassName("forms-v2-editor-scroll" + if (state.activeTab != BuilderTab.Design) " forms-v2-tab-content" else "")
                        div {
                            hidden = (state.activeTab != BuilderTab.Design).unsafeCast<web.html.Hidden>()
                            FormDesignTab {
                                this.state = state; this.controller = controller; canWrite = canEdit
                                openSection = section; onOpenSection = { setSection(it) }
                            }
                        }
                        div {
                            hidden = (state.activeTab != BuilderTab.Settings).unsafeCast<web.html.Hidden>()
                            FormSettingsTab { this.state = state; this.controller = controller; canWrite = canEdit; orgId = props.orgId }
                        }
                        div {
                            hidden = (state.activeTab != BuilderTab.Receipts).unsafeCast<web.html.Hidden>()
                            FormReceiptsTab { this.state = state; this.controller = controller; canWrite = canEdit }
                        }
                    }
                }
            }
            div {
                className = ClassName("forms-v2-preview-column")
                FormDonorPreview {
                    this.state = state; orgName = props.orgName ?: "Your organization"
                    this.editing = editing
                    onEditSection = { openSection(it) }
                }
            }
            if (editing) FormSubmissionFeedRail { this.state = state }
        }
    }
}

internal fun copyFormLink(orgId: String, publicSlug: String) {
    val url = "${kotlinx.browser.window.location.origin}${kotlinx.browser.window.location.pathname}${publicFormUrl(orgId, publicSlug)}"
    js("navigator.clipboard && navigator.clipboard.writeText(url)")
}
